import {useEffect, useState} from "react";
import {Plus, Users, MessageCircle, Flag, ArrowUpRight, CalendarDays, Package} from "lucide-react";
import {SectionHead} from "@/components/UI";
import {SECTION_TILES} from "@/lib/constants";
import {useSite, Rich} from "@/lib/site";
import {apiGet, apiPost} from "@/lib/api";

const ago = (iso) => {
  const h = Math.max(1, Math.round((Date.now() - new Date(iso)) / 36e5));
  return h < 24 ? `${h}h` : `${Math.round(h / 24)}d`;
};

function SectionTiles({go}) {
  const t = useSite();
  return (
    <div className="section-tiles" data-testid="home-section-tiles">
      {SECTION_TILES.filter(([, id]) => t(`show_${id}`) !== false).map(([label, id, Icon, sub]) => (
        <button key={id} data-testid={`home-tile-${id}`} className="section-tile" onClick={() => go(id)}>
          <span><Icon size={19}/></span>
          <strong>{label}</strong>
          <small>{sub}</small>
        </button>
      ))}
    </div>
  );
}

function SummaryTiles({summary, user, go}) {
  const q = summary?.questions?.[0];
  const post = summary?.circle_posts?.[0];
  return (
    <div className="dashboard-stats">
      <button data-testid="home-stat-ask" onClick={() => go("ask", q?.question_id)}>
        <span className="stat-icon coral-bg"><MessageCircle size={18}/></span>
        <b>{String(summary?.questions_this_week ?? 0).padStart(2, "0")}</b><small>New questions this week</small>
        <p className="stat-line">{q ? q.text : "Ask the community anything"}</p>
        <strong>Ask My Community <ArrowUpRight size={14}/></strong>
      </button>
      <button data-testid="home-stat-circles" onClick={() => go("samaj", post?.community_id)}>
        <span className="stat-icon ink-bg"><Users size={18}/></span>
        <b>{String((user ? summary?.circles_joined : summary?.circles_total) ?? 0).padStart(2, "0")}</b><small>{user ? "Samaj joined" : "Public samaj"}</small>
        <p className="stat-line">{post ? `${user && !summary.circles_joined ? "Join one · " : ""}${post.author} in ${post.circle_name}` : "Find your samaj"}</p>
        <strong>Latest posts <ArrowUpRight size={14}/></strong>
      </button>
      <button data-testid="home-stat-gfi" onClick={() => go("gfi")}>
        <span className="stat-icon ink-bg"><Package size={18}/></span>
        <b>{String(summary?.gfi_open_requests ?? 0).padStart(2, "0")}</b><small>Open concierge requests</small>
        <p className="stat-line">{summary?.gfi_open_requests ? "Your concierge is on it" : "Need a hand with anything?"}</p>
        <strong>Your Personal Concierge <ArrowUpRight size={14}/></strong>
      </button>
    </div>
  );
}

function CirclePost({p, go, openMember, notify}) {
  const report = () => apiPost("/reports", {content_type: "message", content_id: p.message_id, reason: "Flagged from home feed"}).then(() => notify("Aabhar — report received")).catch(() => notify("Sign in to report"));
  return (
    <article className="feed-post" data-testid={`home-post-${p.message_id}`}>
      <div className="post-head">
        <div className="avatar small">{p.author?.split(" ").map(x => x[0]).join("").slice(0, 2)}</div>
        <div>
          <button className="author-link" data-testid={`home-post-author-${p.message_id}`} onClick={() => p.user_id && openMember(p.user_id)}>{p.author}</button>
          <small>{p.circle_name} · {ago(p.created_at)}</small>
        </div>
      </div>
      <p>{p.text}</p>
      <div className="post-actions">
        <button data-testid={`home-post-open-${p.message_id}`} onClick={() => go("samaj", p.community_id)}><MessageCircle size={17}/> Reply in samaj</button>
        <button data-testid={`home-post-report-${p.message_id}`} onClick={report}><Flag size={17}/></button>
      </div>
    </article>
  );
}

export default function Home({notify, setModal, user, setActive, openMember}) {
  const [upcoming, setUpcoming] = useState([]);
  const [summary, setSummary] = useState(null);
  const t = useSite();

  useEffect(() => {
    apiGet("/home/summary").then(setSummary).catch(() => {});
    if (user) apiGet("/events/upcoming/me").then(setUpcoming).catch(() => setUpcoming([]));
  }, [user]);

  return (
    <div className="feed-dashboard">
      <div className="dashboard-heading">
        <div>
          <div className="eyebrow">KEM CHO{user ? `, ${user.name.split(" ")[0].toUpperCase()}` : ""} · {new Date().toLocaleDateString("en-GB", {weekday: "long", day: "numeric", month: "long"}).toUpperCase()}</div>
          <h1><Rich text={t("home_title")}/></h1>
          <p>{t("home_sub")}</p>
        </div>
        <button data-testid="home-new-request" className="coral-button" onClick={() => setActive("gfi")}>
          <Plus size={17}/> Your Personal Concierge
        </button>
      </div>

      <SectionTiles go={setActive}/>
      <SummaryTiles summary={summary} user={user} go={setActive}/>

      <div className="feed-layout">
        <section>
          <SectionHead eyebrow="YOUR FEED" title="Fresh from your samaj" action="All samaj" onAction={() => setActive("samaj")}/>
          {(summary?.circle_posts || []).map(p => <CirclePost key={p.message_id} p={p} go={setActive} openMember={openMember} notify={notify}/>)}
          {summary && !summary.circle_posts.length && <p className="admin-help">Join a samaj to see posts here.</p>}
        </section>
        <aside className="right-rail">
          <div className="rail-title"><span>UP NEXT</span></div>
          {upcoming.length > 0 && (
            <div className="rail-card events-rail" data-testid="home-upcoming-events">
              <strong><CalendarDays size={14} style={{display: "inline", marginRight: 6}}/> Events around you</strong>
              {upcoming.slice(0, 3).map(ev => {
                const dt = new Date(ev.when);
                return (
                  <div key={ev.event_id} className="rail-event" data-testid={`rail-event-${ev.event_id}`}>
                    <small>{dt.toLocaleDateString([], {weekday: "short", day: "numeric", month: "short"})} · {dt.toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"})}</small>
                    <p>{ev.title}</p>
                  </div>
                );
              })}
              <button data-testid="home-events-link" onClick={() => setActive("events")}>Open events <ArrowUpRight size={14}/></button>
            </div>
          )}
          <div className="rail-card simple" data-testid="home-ask-rail">
            <strong>Loko puchhe che</strong>
            {(summary?.questions || []).map(q => (
              <button key={q.question_id} className="rail-question" data-testid={`home-question-${q.question_id}`} onClick={() => setActive("ask", q.question_id)}>
                {q.text}<small>{q.answers || 0} answers · {q.author}</small>
              </button>
            ))}
            <button data-testid="home-question" onClick={() => setActive("ask")}>Ask My Community <ArrowUpRight size={15}/></button>
          </div>
          {!user && (
            <div className="rail-card simple" data-testid="home-signin-rail">
              <strong>Padharo! Join Indian Ocean</strong>
              <p>Sign in to join a samaj, ask questions and explore Gujarati culture.</p>
              <button data-testid="home-signin-cta" onClick={() => setModal("auth")}>Sign in <ArrowUpRight size={15}/></button>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
